import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslations } from '@/i18n/use-translations';
import ConfirmationModal from '@/components/common/confirmation-modal';
import EmptyState from '@/components/common/empty-state';
import { useNotifications } from '@/hooks/use-notifications';
import { useListMediaAgentBookingsQuery } from '@/state/queries/media-agent-admin/use-list-media-agent-bookings';
import { useResetMediaAgentBookingMutation } from '@/state/mutations/media-agent-admin/use-reset-media-agent-booking';
import AgentPoolIdCell from '@/components/agent-pools-admin/agent-pool-id-cell';
import type { MediaAgentBookingDto } from '@/types/dtos/media-agent/media-agent-pool.dto';

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function failureReasonLabel(
  reason: MediaAgentBookingDto['failureReason'],
  t: ReturnType<typeof useTranslations>,
): string | null {
  if (!reason) return null;
  const key = `admin.mediaAgentAdmin.bookings.failureReason.${reason}`;
  const fallbacks: Record<NonNullable<MediaAgentBookingDto['failureReason']>, string> = {
    buffer_overlap: 'Another show still holds this agent until its buffer ends',
    no_connected_shell: 'No connected presentation computer available',
    pool_empty: 'No presentation computers in the pool',
    auto_provision_disabled: 'Pool empty and auto-provision is off',
    auto_provision_failed: 'Auto-provision attempt failed',
  };
  return t(key, undefined, fallbacks[reason]);
}

export default function MediaAgentBookingsTable({ enabled }: { enabled: boolean }) {
  const t = useTranslations();
  const notification = useNotifications();
  const [pendingResetId, setPendingResetId] = useState<string | null>(null);
  const { data: bookings = [], isLoading, error } = useListMediaAgentBookingsQuery({ enabled });
  const { mutateAsync: resetBooking, isPending: isResetting } = useResetMediaAgentBookingMutation();

  const handleReset = async () => {
    if (!pendingResetId) return;
    try {
      const result = await resetBooking(pendingResetId);
      notification.notifySuccess(
        t(
          'admin.mediaAgentAdmin.bookings.resetSuccess',
          { status: result.status },
          `Booking reset — status ${result.status}`,
        ),
      );
      setPendingResetId(null);
    } catch {
      notification.notifyError(t('admin.mediaAgentAdmin.bookings.resetError', undefined, 'Failed to reset booking'));
    }
  };

  if (!enabled) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t('admin.mediaAgentAdmin.bookingsTitle', undefined, 'Upcoming bookings')}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t('admin.mediaAgentAdmin.bookingsError', undefined, 'Failed to load bookings')}
        </Alert>
      )}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : bookings.length === 0 ? (
        <EmptyState text={t('admin.mediaAgentAdmin.bookingsEmpty', undefined, 'No upcoming media agent bookings')} />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('admin.mediaAgentAdmin.bookings.reference', undefined, 'Reference')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.bookings.status', undefined, 'Status')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.bookings.window', undefined, 'Show window')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.bookings.buffer', undefined, 'Buffer window')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.bookings.shell', undefined, 'Shell')}</TableCell>
                <TableCell>{t('admin.mediaAgentAdmin.bookings.flow', undefined, 'Flow')}</TableCell>
                <TableCell align="right">{t('admin.mediaAgentAdmin.bookings.actions', undefined, 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking._id} hover>
                  <TableCell>
                    <AgentPoolIdCell id={booking.referenceId} prefix={booking.referenceType} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={booking.status}
                      variant="outlined"
                      color={booking.status === 'failed' ? 'error' : 'default'}
                    />
                    {booking.status === 'failed' && booking.failureReason && (
                      <Tooltip title={failureReasonLabel(booking.failureReason, t) ?? ''}>
                        <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5, maxWidth: 220 }}>
                          {failureReasonLabel(booking.failureReason, t)}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {formatWhen(booking.windowStart)}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatWhen(booking.windowEnd)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" display="block">
                      {formatWhen(booking.bufferStart)}
                    </Typography>
                    <Typography variant="caption" display="block" color="text.secondary">
                      {formatWhen(booking.bufferEnd)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <AgentPoolIdCell id={booking.shellPlayerId} />
                  </TableCell>
                  <TableCell>
                    <AgentPoolIdCell id={booking.flowId} />
                  </TableCell>
                  <TableCell align="right">
                    {(booking.status === 'failed' || booking.status === 'completed') && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={isResetting}
                        onClick={() => setPendingResetId(booking._id)}
                      >
                        {t('admin.mediaAgentAdmin.bookings.reset', undefined, 'Reset')}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmationModal
        open={pendingResetId !== null}
        title={t('admin.mediaAgentAdmin.bookings.resetConfirmTitle', undefined, 'Reset booking?')}
        text={t(
          'admin.mediaAgentAdmin.bookings.resetConfirmText',
          undefined,
          'Reset this media agent booking? Related allocation state may be cleared so the booking can be retried.',
        )}
        confirmLabel={t('admin.mediaAgentAdmin.bookings.reset', undefined, 'Reset')}
        isLoading={isResetting}
        onCancel={() => setPendingResetId(null)}
        onConfirm={() => void handleReset()}
      />
    </Box>
  );
}
